import { useState, useEffect, useRef, type CSSProperties } from "react";
import { cn } from "@/lib/utils";

interface ProgressCircleProps {
  color?: string;
  progress: number;
  className?: string;
  duration?: number;
  delay?: number;
}

export const ProgressCircle = ({
  color = "#6d28d9",
  progress,
  className,
  duration = 500,
  delay = 0,
}: ProgressCircleProps) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const animationFrameRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const progressRef = useRef(progress);
  const previousProgressRef = useRef(0); // Store the previous progress value

  useEffect(() => {
    previousProgressRef.current = animatedProgress; // Store current progress before updating
    progressRef.current = progress;
  }, [progress, animatedProgress]);

  useEffect(() => {
    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime;
      }

      const timeElapsed = currentTime - startTimeRef.current;
      const progressValue = Math.min(timeElapsed / duration, 1);

      // Interpolate between previous and target progress
      const currentProgress =
        previousProgressRef.current +
        (progressRef.current - previousProgressRef.current) * progressValue;

      setAnimatedProgress(currentProgress);

      if (timeElapsed < duration) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setAnimatedProgress(progressRef.current);
        startTimeRef.current = null;
      }
    };

    const startAnimation = () => {
      startTimeRef.current = null;
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const timeoutId = setTimeout(() => {
      startAnimation();
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [progress, duration, delay]);

  const gradient = `conic-gradient(${color} ${animatedProgress}%, transparent 0deg)`;

  return (
    <span className={cn("flex items-center transition-colors text-inherit", className)}>
      <span className="inline-flex items-center gap-1">
        <span
          className={cn(
            "progress-circle relative inline-block w-[1.15em] h-[1.15em] rounded-full border border-primary",
            "before:absolute before:content-[''] before:inset-[2px]",
            "transition-opacity duration-200",
            progress === 0 ? "opacity-30" : "opacity-100",
          )}
          style={
            {
              ["--my-gradient" as string]: gradient,
              border: `1px solid ${color}`,
            } as CSSProperties
          }
        />
      </span>
    </span>
  );
};
